import {
  AutosuggestProps,
  NonCancelableCustomEvent,
  SelectProps,
  Select,
} from '@amzn/awsui-components-core';
import { UserType } from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import PortalServiceClient from '../../api/PortalServiceClient';
import constants from '../../common/constants';
import { withAppContext } from '../../contexts/AppContext';
import AppContextProps from '../../contexts/AppContextProps';
import { localLog } from '../../loggers/Logger';
import { extractUserAlias } from '../../api/UtilsApi';
import arraySort from 'array-sort';

const MIN_INPUT_LENGTH = 3;
const MAX_INPUT_LENGTH = 64;

export interface SearchUserProfile {
  userId: string;
  name: string;
  type?: string;
  email?: string;
}

export interface AdditionlUserInfo {
  email?: string;
  name?: string;
  type?: string;
}

interface Props extends AppContextProps {
  userId?: string;
  userName?: string;
  onUserSelected: (
    selectedUserId: string | undefined,
    selectedUserName: string | undefined,
    validationError: string | undefined,
    additionalInfo?: AdditionlUserInfo,
  ) => void;
  isRequired?: boolean;
}

interface State {
  userName?: string;
  userInput?: string;
  userSearchAutoSuggestions: AutosuggestProps.Option[];
  isSearchingForUsers?: boolean;
  isInvalid: boolean;
  showValidation?: any;
  errorMessage?: string;
}

class UserIdAutosuggest extends React.Component<Props, State> {
  static defaultProps = {
    isRequired: true,
  };

  private portalService: PortalServiceClient;
  private mplService: IMplServiceClient;

  private currentRequest: Promise<any> | undefined = Promise.resolve([[], []]);

  private requestQueueLength: number = 0;

  private searchResultsByUserId = new Map<string, SearchUserProfile>();

  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    this.state = {
      isInvalid: false,
      userInput: props.userId,
      userName: props.userName,
      userSearchAutoSuggestions: [],
    };
    this.portalService = PortalServiceClient.create();
    this.mplService = MplServiceClientFactory.create();
  }

  public render() {
    const {
      userName,
      userInput,
      userSearchAutoSuggestions,
      isSearchingForUsers,
      isInvalid,
      showValidation,
    } = this.state;

    let statusType: AutosuggestProps.StatusType | undefined;
    if (isSearchingForUsers) {
      statusType = 'loading';
    } else if (isInvalid && showValidation) {
      statusType = 'error';
    }
    let valueOption = userSearchAutoSuggestions.find((option) => option.label === userName);
    if (!valueOption) {
      valueOption = userSearchAutoSuggestions.find((option) => option.value === userInput);
    }
    // Show a fallback option when a userId is pre-populated but no search results exist yet
    if (!valueOption && userInput) {
      valueOption = { value: userInput, label: userName || extractUserAlias(userInput) };
    }

    return (
      <Select
        selectedOption={valueOption ?? null}
        options={userSearchAutoSuggestions}
        placeholder={constants.USER_SEARCH_PLACEHOLDER}
        empty={constants.USER_SEARCH_EMPTY_INFO}
        onChange={this.setSelectedUser}
        onLoadItems={({ detail }) => this.updateUserAutosuggest(detail.filteringText)}
        onFocus={this.hideValidation}
        onBlur={this.showValidation}
        filteringType="manual"
        invalid={isInvalid && showValidation}
        statusType={statusType}
        ariaRequired
      />
    );
  }

  private updateUserAutosuggest = async (input: string) => {
    let autosuggestOptions: AutosuggestProps.Option[] = [];
    let searchResults: SearchUserProfile[] = [];

    if (input.length >= MIN_INPUT_LENGTH && input.length <= MAX_INPUT_LENGTH) {
      try {
        this.setState({
          isSearchingForUsers: true,
        });

        // Ensures only one request is pending when user keeps typing and no unnecessary requests are being made.
        // All accumulating changes are queued and only the last one is executed when the previous request finshed.
        if (this.currentRequest) {
          const queuePosition = ++this.requestQueueLength;
          await this.currentRequest;
          // Check if this input change event was the most recent to trigger the search request.
          if (queuePosition < this.requestQueueLength) {
            return;
          } else {
            this.currentRequest = undefined;
            this.requestQueueLength = 0;
          }
        }

        const requests = [
          // Search for exact email match
          this.portalService.searchUserProfileByEmail(input).catch((e) => {
            localLog.error('CAT Portal email search request failed');
            return [];
          }),
          this.props.appContext.authnInfo?.userProfile.type === UserType.AWS
            ? // Swallow errors from user search through APGL service/People API to avoid surfacing
              // errors from integraiton issues to users. Portal search results are available in any case.
              this.mplService.searchUsers(input).catch((e) => {
                localLog.error('APGL service user search request failed');
                return [];
              })
            : Promise.resolve([]),
        ];

        const perfectMatchFilter = (results: SearchUserProfile[]) => {
          const perfectMatchCondition = (r: SearchUserProfile) =>
            r.name?.toLocaleLowerCase() === this.state.userInput?.toLocaleLowerCase();
          const hasPerfectMatches = results.some(perfectMatchCondition);
          if (hasPerfectMatches) {
            return results.filter(perfectMatchCondition);
          } else {
            return results;
          }
        };

        const request = Promise.all(requests);
        this.currentRequest = request;
        const [portalResults, mplResults] = await request;

        // Show PAPI results and partner results from Portal for AWS useres
        // Show partner and AWS results from Portal to partner users (no PAPI results)
        // TODO - Revisit after fixing PAPI search issue
        // const filteredPortalResults = portalResults.filter(pr => {
        //     if (this.props.appContext.authnInfo?.userProfile.type === UserType.AWS) {
        //         return pr.type === UserType.Partner;
        //     } else {
        //         return true;
        //     }
        // });

        // Show both PAPI and Portal results to AWS users
        // Show only exact email address match from Portal to Partner user.
        const combinedResults =
          this.props.appContext.authnInfo?.userProfile.type === UserType.AWS
            ? // @ts-expect-error TS2488 - strict migration
              [...mplResults, ...portalResults]
            : portalResults;

        // @ts-expect-error TS18048 - strict migration
        const uniqueIdUsers = combinedResults.filter(
          (val, idx, self) => self.findIndex((v) => v.userId === val.userId) === idx,
        );
        searchResults = perfectMatchFilter(uniqueIdUsers);

        autosuggestOptions = searchResults.map((mr) => {
          let typeDisplay = mr.type;
          if (mr.type === 'Amazon' || mr.type === 'AWS') {
            const amazonAlias = mr.userId.split('_')[1];
            typeDisplay = `Amazon: ${amazonAlias}@`;
          }
          return {
            label: `${mr.name} (${typeDisplay})`,
            value: mr.userId,
            description: mr.email,
          };
        });
        autosuggestOptions = arraySort(autosuggestOptions, 'label');
      } catch (err) {
        localLog.error({ err }, 'User search request failed');
        this.setState({
          // @ts-expect-error TS18046 - strict migration
          errorMessage: err.message || 'User search request failed.',
          isSearchingForUsers: false,
        });
        this.currentRequest = undefined;
        this.requestQueueLength = 0;
      }
    }

    // Discard results as another search request is already queued up
    if (this.requestQueueLength > 0) {
      return;
    }

    // Keep the lookup in step with the options actually shown, so a selection resolves to its own result.
    this.searchResultsByUserId = new Map(searchResults.map((r) => [r.userId, r]));
    this.setState({
      userSearchAutoSuggestions: autosuggestOptions,
      isSearchingForUsers: false,
    });
  };

  private showValidation = () => {
    this.setState({
      showValidation: true,
    });
  };

  private hideValidation = () => {
    this.setState({
      showValidation: false,
    });
  };

  // @ts-expect-error TS7030 - strict migration
  private validateUserId = (userId: any): string | undefined => {
    const { isRequired } = this.props;
    const allowedUserIdPrefixes = /^(AmazonCorporate_|LoginWithAmazon_|APN_).{1,}$/;
    if ((isRequired && !userId) || (userId && !allowedUserIdPrefixes.test(userId))) {
      this.setState({
        isInvalid: true,
      });
      return `Invalid user ID "${userId}.".`;
    }
    this.setState({
      isInvalid: false,
    });
  };

  private setSelectedUser = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
    if (event.detail.selectedOption) {
      const selectedUserId = event.detail.selectedOption.value;
      const selectedUserName = event.detail.selectedOption.label;
      const searchResult = selectedUserId
        ? this.searchResultsByUserId.get(selectedUserId)
        : undefined;
      const additionalInfo = {
        email: event.detail.selectedOption.description,
        name: searchResult?.name,
        type: searchResult?.type,
      };

      this.setState({
        userInput: selectedUserId,
        userName: selectedUserName,
      });

      const validationError = this.validateUserId(selectedUserId);

      if (this.props.onUserSelected) {
        this.props.onUserSelected(
          selectedUserId,
          selectedUserName,
          validationError,
          additionalInfo,
        );
      }
    } else {
      this.setState({
        userInput: undefined,
        userName: undefined,
      });

      if (this.props.onUserSelected) {
        this.props.onUserSelected(undefined, undefined, 'invalid user');
      }
    }
  };
}

export default withAppContext(UserIdAutosuggest);
